import { useState, useEffect, useCallback } from 'react';
import { axiosApi } from '../../api/setupApi';

function useNisMasterlist(year, iaName, isName) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNisMasterlist = useCallback(async () => {
    if (!year) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const hasContext = Boolean(iaName || isName);

      if (hasContext) {
        const { data } = await axiosApi.get(`/nisMasterlist/by-context/${year}`, {
          params: {
            ia_name: iaName,
            ...(isName ? { is_name: isName } : {})
          }
        });

        // Normalize filtered flat rows into the nested shape consumed by nisMasterlist.js
        const groupedByIs = (data || []).reduce((acc, row) => {
          const currentIs = row.is_name || 'Unknown';
          if (!acc[currentIs]) {
            acc[currentIs] = {};
          }

          const currentIa = row.ia_name || 'Unknown IA';
          if (!acc[currentIs][currentIa]) {
            acc[currentIs][currentIa] = [];
          }

          acc[currentIs][currentIa].push(row);
          return acc;
        }, {});

        const normalized = Object.entries(groupedByIs).map(([currentIs, iaMap]) => ({
          is_name: currentIs,
          ias: Object.entries(iaMap).map(([currentIa, rows]) => ({
            ia_name: currentIa,
            rows
          }))
        }));

        if (normalized.length > 0) {
          setData(normalized);
          return;
        }

        // Fallback: fetch the year-scoped nested data and filter locally using normalized matching.
        const { data: nestedData } = await axiosApi.get(`/nisMasterlist/nested/${year}`);
        setData(nestedData);
      } else {
        const { data } = await axiosApi.get(`/nisMasterlist/nested/${year}`);
        setData(data);
      }
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }, [year, iaName, isName]);

  useEffect(() => {
    fetchNisMasterlist();
  }, [fetchNisMasterlist]);

  return { data, loading, error, refetch: fetchNisMasterlist };
}

export default useNisMasterlist;
